import {KeysOfType, ValueOfArray, Nullable, NullableKeys} from '@fl/cmsch-fe-library';
import {includes} from 'lodash/fp';

import {NullableNumberPair} from 'app/mating/types/nullable-number-pair';

/** Redux form name for the calculation-parameter form. */
export const matingParamsSelectionFormName = 'matingParamsSelection';

/** Form values used by the calculation-parameter form. */
export type MatingParamsSelectionFormValues = NullableKeys<{
    // left column
    /** Inbreeding coefficient threshold. */
    RxyMaxU: number,

    /** Production trait weight. */
    VhMlk: number,
    /** Production trait weight. */
    VhTuk: number,
    /** Production trait weight. */
    VhBlk: number,
    /** Health trait weight. */
    VhSB: number,
    /** Health trait weight. */
    VhKM: number,
    /** Health trait weight. */
    VhOCD: number,
    /** Functional trait weight. */
    VhDlh: number,
    /** Functional trait weight. */
    VhOOP1: number,
    /** Functional trait weight. */
    VhDoj: number,
    /** Functional trait weight. */
    VhPld: number,

    // middle column
    /** Exterior trait weight. */
    TRA: NullableNumberPair,
    /** Exterior trait weight. */
    SHR: NullableNumberPair,
    /** Exterior trait weight. */
    HLT: NullableNumberPair,
    /** Exterior trait weight. */
    HRA: NullableNumberPair,
    /** Exterior trait weight. */
    SKZ: NullableNumberPair,
    /** Exterior trait weight. */
    SRZ: NullableNumberPair,
    /** Exterior trait weight. */
    PZZ: NullableNumberPair,
    /** Exterior trait weight. */
    PZB: NullableNumberPair,
    /** Exterior trait weight. */
    PAZ: NullableNumberPair,
    /** Exterior trait weight. */
    PUV: NullableNumberPair,
    /** Exterior trait weight. */
    RPS: NullableNumberPair,
    /** Exterior trait weight. */
    DST: NullableNumberPair,
    /** Exterior trait weight. */
    HLV: NullableNumberPair,
    /** Exterior trait weight. */
    VZU: NullableNumberPair,
    /** Exterior trait weight. */
    ZAV: NullableNumberPair,
    /** Exterior trait weight. */
    RZS: NullableNumberPair,
    /** Exterior trait weight. */
    SZU: NullableNumberPair,
    /** Exterior trait weight. */
    KVK: NullableNumberPair,
    /** Exterior trait weight. */
    CHO: NullableNumberPair,
    /** Exterior trait weight. */
    KND: NullableNumberPair,

    // right column
    /** Production-group weight. */
    VhProd: number,
    /** Exterior-group weight. */
    VhExt: number,
    /** Functional-group weight. */
    VhFnkcn: number,
    /** Health-group weight. */
    VhZdr: number,
}>;

/** Coefficient form keys. */
/** Form fields that map to the inbreeding coefficient criteria. */
export const matingParamsCoefficientCodes: Array<KeysOfType<MatingParamsSelectionFormValues, Nullable<number>>> =
    ['RxyMaxU'];
/**
 * Checks whether a form code belongs to the coefficient group.
 *
 * @param x Candidate code.
 * @returns `true` when the code is a coefficient field.
 */
export const isMatingParamsCoefficient = (x: string): x is ValueOfArray<typeof matingParamsCoefficientCodes> =>
    includes(x, matingParamsCoefficientCodes);

/** Form fields that map to scoring-weight criteria. */
export const matingParamsScoringWeightsCodes: Array<KeysOfType<MatingParamsSelectionFormValues, Nullable<number>>> = [
    'VhMlk',
    'VhTuk',
    'VhBlk',
    'VhSB',
    'VhKM',
    'VhOCD',
    'VhDlh',
    'VhOOP1',
    'VhDoj',
    'VhPld',
    'VhProd',
    'VhExt',
    'VhFnkcn',
    'VhZdr',
];
/**
 * Checks whether a form code belongs to the scoring-weight group.
 *
 * @param x Candidate code.
 * @returns `true` when the code is a scoring-weight field.
 */
export const isMatingParamsScoringWeights = (x: string): x is ValueOfArray<typeof matingParamsScoringWeightsCodes> =>
    includes(x, matingParamsScoringWeightsCodes);

/** Form fields that map to exterior-trait criteria. */
export const matingParamsExteriorTraitWeightsCodes:
Array<KeysOfType<MatingParamsSelectionFormValues, Nullable<NullableNumberPair>>> = [
    'TRA',
    'SHR',
    'HLT',
    'HRA',
    'SKZ',
    'SRZ',
    'PZZ',
    'PZB',
    'PAZ',
    'PUV',
    'RPS',
    'DST',
    'HLV',
    'VZU',
    'ZAV',
    'RZS',
    'SZU',
    'KVK',
    'CHO',
    'KND',
];
/**
 * Checks whether a form code belongs to the exterior-trait group.
 *
 * @param x Candidate code.
 * @returns `true` when the code is an exterior-trait field.
 */
export const isMatingParamsExteriorTraitWeights =
    (x: string): x is ValueOfArray<typeof matingParamsExteriorTraitWeightsCodes> =>
        includes(x, matingParamsExteriorTraitWeightsCodes);
